import React, { useState } from "react";
import VideoStream from "../components/VideoStream";
import VideoDescription from "../components/VideoDescription";
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  clearChurchDetailData,
  getChurchById,
  getChurchWFavoriteById,
} from "../slices/church.slice";
import favoriteService from "../services/favorite.service";
import toast from "react-hot-toast";
import _ from "lodash";
import Loading from "../components/Animations/Loading";
import FavoriteStreamsCard from "../components/Card/FavoriteStreamsCard";
import ExtraFooter from "../components/ExtraFooter";
import { createWebLink, getDomainName, ucword } from "../lib/helper";
import useIsSuperAdmin from "../hooks/useIsSuperAdmin";
import BaseButton from "../components/Buttons/BaseButton";
import { MdEditNote } from "react-icons/md";
import ReactGA from "react-ga";
import RecentVideos from "../components/RecentVideos";
import ChurchInfo from "../components/ChurchInfo";

const ChurchDetail = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const [showSuperAdminBoard] = useIsSuperAdmin();
  const church = useSelector((state) => state.churchData.church);
  const [loading, setLoading] = useState(false);
  const [favoriteNote, setFavoriteNote] = useState();
  const [editNote, setEditNote] = useState(false);
  const { churchId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isFavoriteChurch =
    church && church.favorites && _.isEmpty(church.favorites) ? false : true;

  useEffect(() => {
    authorizeChurchDetail();
    return () => {
      dispatch(clearChurchDetailData());
    };
  }, [dispatch, churchId, searchParams]);

  useEffect(() => {
    ReactGA.pageview("/church-detail", [churchId]);
  }, []);

  const currentVideoId = searchParams.get("v")
    ? searchParams.get("v")
    : church?.streams[0]?.videoId;

  const currentStream = () => {
    //return default
    if (!currentVideoId) {
      return church?.streams[0];
    }
    return church?.streams?.filter(
      (stream) => stream.videoId === currentVideoId
    )[0];
  };

  const getCurrentNote = () => {
    return church.notes.filter(
      (note) => note.stream.videoId === currentVideoId
    );
  };

  const authorizeChurchDetail = () => {
    if (currentUser) {
      return dispatch(
        getChurchWFavoriteById({
          churchId,
          userId: currentUser.id,
          currentVideoId,
        })
      );
    } else {
      return dispatch(getChurchById({ churchId }));
    }
  };

  const onAddFavorite = async () => {
    setLoading(true);
    if (!currentUser) {
      navigate(`../../signin?redirect=${window.location.pathname}`);
      return;
    }
    const favorite = {
      churchId,
      userId: currentUser.id,
      note: favoriteNote,
    };
    try {
      const response = await favoriteService.addFavorite(favorite);
      if (response.status === 200) {
        authorizeChurchDetail();
        toast.success(response.data.message);
        setLoading(false);
      }
      setLoading(false);
    } catch (error) {
      toast.error(error.response.data.message);
      setLoading(false);
    }
  };

  const onRemoveFavorite = async () => {
    const favoriteId = church.favorites[0].id;

    try {
      const response = await favoriteService.removeFavorite(favoriteId);
      if (response.status === 200) {
        authorizeChurchDetail();
        toast.success(response.data.message);
        setLoading(false);
      }
      setLoading(false);
    } catch (error) {
      toast.error(error.response.data.message);
      setLoading(false);
    }
  };

  const onAddFavoriteStream = async () => {
    const currentStreamId = currentStream().id;
    setLoading(true);
    const favorite = {
      churchId,
      userId: currentUser.id,
      streamId: currentStreamId,
      videoId: currentVideoId,
    };
    try {
      const response = await favoriteService.addFavoriteStream(favorite);
      if (response.status === 200) {
        authorizeChurchDetail();
        toast.success(response.data.message);
        setLoading(false);
      }
      setLoading(false);
    } catch (error) {
      toast.error(error.response.data.message);
      setLoading(false);
    }
  };

  const currentFavoriteStream = () =>
    church.favoriteStreams.filter(
      (stream) => stream.videoId === currentVideoId
    );

  const onRemoveFavoriteStream = async () => {
    const favoriteId = currentFavoriteStream()[0].id;
    setLoading(true);
    try {
      const response = await favoriteService.removeFavoriteStream(favoriteId);

      if (response.status === 200) {
        authorizeChurchDetail();
        toast.success(response.data.message);
        setLoading(false);
      }
      setLoading(false);
    } catch (error) {
      toast.error(error.response.data.message);
      setLoading(false);
    }
  };

  const onUpdateNote = async () => {
    setLoading(true);
    try {
      const response = await favoriteService.updateStreamNote({
        noteId: getCurrentNote()[0].id,
        note: favoriteNote,
      });
      if (response.status === 200) {
        authorizeChurchDetail();
        toast.success(response.data.message);
        setLoading(false);
        setEditNote(false);
      }
      setLoading(false);
    } catch (error) {
      toast.error(error.response.data.message);
      setLoading(false);
    }
  };

  const onAddNote = async () => {
    const currentStreamId = currentStream().id;
    try {
      const response = await favoriteService.addStreamNote({
        currentStreamId,
        churchId,
        userId: currentUser.id,
        note: favoriteNote,
      });
      if (response.status === 200) {
        authorizeChurchDetail();
        toast.success(response.data.message);
        setLoading(false);
        setEditNote(false);
      }
      setLoading(false);
    } catch (error) {
      toast.error(error.response.data.message);
      setLoading(false);
    }
  };

  const isFavoriteStream = () => {
    if (!church?.favoriteStreams) {
      return false;
    }
    const favFilter = church.favoriteStreams.filter(
      (stream) => stream.videoId === currentVideoId
    );
    return favFilter.length !== 0;
  };

  const wait = () => {
    while (!church) {
      return <Loading size={6} />;
    }

    const leadPastors = church?.churchPastorConfigs
      ?.filter((item) => item.type == "LEAD")
      .map((item) => item?.pastorDetail.name);
    const otherPastors = church?.churchPastorConfigs
      ?.filter((item) => item.type == "OTHER")
      .map((item) => item?.pastorDetail.name);
    console.log(leadPastors);
    return (
      <>
        <div className="flex flex-col md:flex-row md:p-6">
          <div className="md:w-4/5 church-details-video-left">
            {church.streams && (
              <VideoStream
                videoId={currentVideoId}
                onAddFavoriteStream={onAddFavoriteStream}
                onRemoveFavoriteStream={onRemoveFavoriteStream}
                loading={loading}
                currentUser={currentUser}
                isFavoriteStream={isFavoriteStream()}
                onRemoveFavorite={onRemoveFavorite}
                onAddFavorite={onAddFavorite}
                socials={church.socials}
                church={church}
                isFavoriteChurch={isFavoriteChurch}
              />
            )}
          </div>
          <div className="md:w-1/5 flex flex-col justify-between church-details-video-right">
            <VideoDescription
              church={church}
              currentUser={currentUser}
              setFavoriteNote={setFavoriteNote}
              editNote={editNote}
              setEditNote={setEditNote}
              onUpdateNote={onUpdateNote}
              currentVideoId={currentVideoId}
              onAddNote={onAddNote}
            />

            {showSuperAdminBoard && (
              <div className="flex flex-row justify-between px-2">
                <BaseButton
                  kind="dark"
                  text={
                    <div className="flex flex-row items-center gap-x-1">
                      <MdEditNote size={20} /> Edit Church
                    </div>
                  }
                  onClick={() =>
                    navigate(`/dashboard/edit-church/${church.id}`)
                  }
                />
              </div>
            )}
          </div>
        </div>
        <div className="grid md:grid-cols-2 md:m-8">
          <ChurchInfo church={church} />
          <RecentVideos church={church} />
        </div>
      </>
    );
  };
  return <div>{wait()}</div>;
};

export default ChurchDetail;
